import { RouteLocationNormalized } from 'vue-router';
import { PageSectionType } from '../enums/page-section-type';
import { IGetters } from '../interfaces/store.interfaces';
import { PortalType } from '../enums/portal-type';
import globals from '@/utils/globals';
import store from '.';
import IStep from '@/interfaces/step.interface';
import { JourneyMenuTab } from '@/enums/journey-menu-tab';
import { IJourneyState } from '@studyportals/studentdomain/src/i-journey-state';
import { AttendanceType } from '@studyportals/studentdomain';
import { StepState } from '@/enums/step-states';
import { StepType } from '../../interfaces/enums/step-type';
import { ScholarshipRequirementType, CurrentEligibilityScholarshipDTO } from '@studyportals/eligibility-model';
import { ScholarshipItemStatus } from '@/enums/scholarship-item-status';

const getters: IGetters = {
	activePageSection: (): PageSectionType => store.state.activePageSection,
	name: (): string => store.state.name,
	email: (): string => store.state.email,
	countriesOfInterest: (): number[] => store.state.countriesOfInterest,
	disciplinesOfInterest: (): number[] => store.state.disciplinesOfInterest,
	attendance: (): AttendanceType[] => store.state.attendance,
	residenceCountry: (): string => store.state.residenceCountry,
	nationalityCountry: (): string => store.state.nationalityCountry,
	startDate: (): string => store.state.startDate,
	profileCompleteness: (): number => store.state.profileCompleteness,
	isInitialStudentDataLoaded: (): boolean => store.state.isInitialStudentDataLoaded,
	genderImageProperties: (): { imgUrl: string; imgAlt: string } => {
		switch (store.state.gender) {
		case 'm':
			return {
				imgUrl: `${globals.monetBaseURL}/gender_m.svg`,
				imgAlt: 'gender_male',
			};
		case 'f':
			return {
				imgUrl: `${globals.monetBaseURL}/gender_f.svg`,
				imgAlt: 'gender_female',
			};
		default:
			return {
				imgUrl: `${globals.monetBaseURL}/gender_n.svg`,
				imgAlt: 'gender_neutral',
			};
		}
	},
	portalType: (): PortalType => {
		const url: URL = new URL(window.location.toString());

		if (url.host.includes('mastersportal')) {
			return PortalType.MASTERSPORTAL;
		}

		if (url.host.includes('bachelorsportal')) {
			return PortalType.BACHELORSPORTAL;
		}

		if (url.host.includes('phdportal')) {
			return PortalType.PHDPORTAL;
		}

		if (url.host.includes('shortcoursesportal')) {
			return PortalType.SHORTCOURSESPORTAL;
		}

		if (url.host.includes('distancelearningportal')) {
			return PortalType.DISTANCELEARNINGPORTAL;
		}

		return PortalType.MASTERSPORTAL;
	},
	isLoggedIn: (): boolean => store.state.isLoggedIn,
	journeySteps: (): IStep[] => store.state.journeySteps,
	getActiveJourneyStep: (): IStep | null => {
		const journeySteps = store.getters.journeySteps();
		let currentStep = journeySteps.find(
			(step: IStep) => step.stepState() === StepState.IN_PROGRESS || step.stepState() === StepState.READY_TO_COMPLETE,
		);

		if (!currentStep) {
			currentStep = journeySteps[0];
		}

		return currentStep ?? null;
	},
	getCurrentJourneyMenuTab: (): JourneyMenuTab => store.state.currentJourneyMenuTab,
	getWishlistStudyCount: (): number => store.state.wishlistStudyCount,
	getJourneyState: (): IJourneyState | null => store.state.journeyState,
	getEvaluateView: (): boolean => store.state.evaluateView,
	getStepByType: (stepType: StepType): IStep | null => {
		return store.state.journeySteps.find((step: IStep) => step.stepType === stepType) ?? null;
	},
	getHasFavouritesInIsApplyingState: (): boolean => store.state.numberOfFavouritesInIsApplyingState > 0,
	getNumberOfFavouritesInIsApplyingState: (): number => store.state.numberOfFavouritesInIsApplyingState,
	getNumberOfFavouritesHasAppliedState: (): number => store.state.numberOfFavouritesHasAppliedState,
	getHasFavouritesInHasApplyingState: (): boolean => store.state.numberOfFavouritesHasAppliedState > 0,
	getCreatingApplicationChecklistView: (): boolean => store.state.creatingApplicationChecklistView,
	getCurrentJourneyStep: (): IStep | null => store.state.currentStep,
	getCurrentJourneyStepType: (): StepType => {
		const currentStep = store.getters.getCurrentJourneyStep();
		if (!currentStep) {
			return store.getters.getActiveJourneyStep()?.stepType || StepType.DISCOVER;
		}
		return currentStep.stepType;
	},
	getEmailPreferences: (): boolean => {
		return (
			store.state.emailingFavourites &&
			store.state.emailingOther &&
			store.state.emailingScholarships &&
			store.state.emailingUpdates
		);
	},
	getScholarshipEligibility: (): CurrentEligibilityScholarshipDTO => store.state.scholarshipEligibility,
	getScholarshipEligibilityStatusByType: (scholarshipType: ScholarshipRequirementType): ScholarshipItemStatus => {
		const eligibility = store.getters.getScholarshipEligibility();
		let shouldApplicationLetterBeAvailable = true;

		for (const key in eligibility) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
			if (!eligibility[key] && key !== ScholarshipRequirementType.APPLICATION_LETTER) {
				shouldApplicationLetterBeAvailable = false;
			}
		}

		if (scholarshipType === ScholarshipRequirementType.APPLICATION_LETTER) {
			return shouldApplicationLetterBeAvailable
				? ScholarshipItemStatus.PARTIAL_CONFIRMATION
				: ScholarshipItemStatus.NOT_AVAILABLE;
		}

		const isEligible = eligibility[scholarshipType];

		if (!isEligible) {
			return ScholarshipItemStatus.PENDING;
		}

		return ScholarshipItemStatus.COMPLETED;
	},
	getScholarshipEligibleCompletedCount: (): number => {
		const eligibility = store.getters.getScholarshipEligibility();
		let count = 0;

		for (const key in eligibility) {
			if (eligibility[key]) {
				count++;
			}
		}

		return count;
	},
	getCurrentRoute: (): RouteLocationNormalized | null => store.state.currentRoute,
	getIsMenuActive: (): boolean => store.state.isMenuActive,
	getScholarshipEligibilityFlagsCount: (): number => {
		const eligibilityFlags = store.state.scholarshipEligibilityFlags;

		return Object.keys(eligibilityFlags).length;
	},
};

export default getters;
